import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  createUser,
  deleteUser,
  listUsers,
  updateUser,
  type User,
  type UserCreate,
  type UsersListParams,
  type UserUpdate,
} from "@/lib/api";

export const usersKeys = {
  all: ["users"] as const,
  list: (params?: UsersListParams) =>
    [...usersKeys.all, "list", params] as const,
};

export function useUsers(
  params?: UsersListParams,
): UseQueryResult<User[], Error> {
  return useQuery({
    queryKey: usersKeys.list(params),
    queryFn: () => listUsers(params),
  });
}

export function useCreateUser(): UseMutationResult<User, Error, UserCreate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UserCreate) => createUser(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

export function useUpdateUser(): UseMutationResult<
  User,
  Error,
  { id: number; body: UserUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => updateUser(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

export function useDeleteUser(): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}
